<template>
  <div>
    <label class="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
      {{ label }}
      <span
        v-if="optional"
        class="text-xs font-normal text-gray-500 dark:text-gray-400"
      >
        ({{ $t('app.users.modal.password_optional') }})
      </span>
    </label>
    <div class="relative">
      <input
        :value="modelValue"
        type="password"
        :required="required"
        :minlength="minlength"
        :placeholder="placeholder"
        :class="[
          'w-full pl-10 pr-3 py-2 sm:py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white transition-colors',
          hasError ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-700'
        ]"
        @input="handleInput"
        @blur="handleBlur"
      >
    </div>
    
    <!-- Password validation errors -->
    <div
      v-if="hasErrors && touched"
      class="mt-1.5 sm:mt-2 space-y-0.5 sm:space-y-1 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800"
    >
      <p
        v-for="(error, index) in errors"
        :key="index"
        class="text-xs text-red-600 dark:text-red-400 flex items-start gap-1.5"
      >
        <XCircleIcon class="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>{{ error }}</span>
      </p>
    </div>
      
    <!-- Password hint (only show if no errors or not touched) -->
    <p
      v-else-if="hint"
      class="mt-1.5 text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5"
    >
      <InformationCircleIcon class="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-0.5" />
      <span>{{ hint }}</span>
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { LockClosedIcon, XCircleIcon, InformationCircleIcon } from '@heroicons/vue/24/outline';

interface Props {
  modelValue: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  optional?: boolean;
  minlength?: number;
  hint?: string;
  errors?: string[];
  touched?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  required: false,
  optional: false,
  minlength: undefined,
  hint: '',
  errors: () => [],
  touched: false
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  blur: [];
  input: [];
}>();

const hasError = computed(() => {
  return props.errors.length > 0;
});

const hasErrors = computed(() => {
  return props.errors.length > 0;
});

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', target.value);
  emit('input');
}

function handleBlur() {
  emit('blur');
}
</script>
